// src/components/Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import SearchPage from './SearchPage';
import { QRCodeCanvas } from 'qrcode.react';

const Navbar = ({ 
  logoColor, 
  logoUrl, 
  primaryColor, 
  secondaryColor, 
  username, 
  onSearch, 
  products, 
  contacts, 
  categories,
  language = 'en' // 'en' or 'km'
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showSearchPage, setShowSearchPage] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrSize, setQrSize] = useState(200);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  const currentUrl = window.location.href;

  // Translations
  const translations = {
    en: {
      store: `${username}'s Store`,
      home: 'Home',
      products: 'Products',
      contact: 'Contact',
      search: 'Search products...',
      viewAll: 'View all results',
      shareStore: 'Share Store',
      copyLink: 'Copy Link',
      share: 'Share',
      download: 'Download',
      readyToPurchase: 'Ready to Purchase?',
      contactUs: 'Contact us through any platform',
      cancel: 'Cancel',
      noContacts: 'No contact information available',
      buyNow: 'Buy Now',
      close: 'Close'
    },
    km: {
      store: `ហាង ${username}`,
      home: 'ទំព័រដើម',
      products: 'ផលិតផល',
      contact: 'ទំនាក់ទំនង',
      search: 'ស្វែងរកផលិតផល...',
      viewAll: 'មើលលទ្ធផលទាំងអស់',
      shareStore: 'ចែករំលែកហាង',
      copyLink: 'ចម្លងតំណ',
      share: 'ចែករំលែក',
      download: 'ទាញយក',
      readyToPurchase: 'ត្រៀមទិញទេ?',
      contactUs: 'ទាក់ទងមកយើងតាមរយៈបណ្តាញណាមួយ',
      cancel: 'បោះបង់',
      noContacts: 'មិនមានព័ត៌មានទំនាក់ទំនង',
      buyNow: 'ទិញឥឡូវ',
      close: 'បិទ'
    }
  };

  const t = translations[language] || translations.en;

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const filtered = products?.filter(product => 
      product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
    ) || [];
    setSearchResults(filtered.slice(0, 5));
    setShowSearchResults(true);
  }, [searchTerm, products]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setShowSearchPage(true);
      setShowSearchResults(false);
      setShowMobileSearch(false);
      if (onSearch) {
        onSearch(searchTerm);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const handleProductClick = (product) => {
    setSearchTerm('');
    setShowSearchResults(false);
    setShowSearchPage(false);
    setShowMobileSearch(false);
    if (onSearch) {
      onSearch('');
    }
    const productElement = document.getElementById(`product-${product.id}`);
    if (productElement) {
      productElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleCloseSearchPage = () => {
    setShowSearchPage(false);
    setSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };

  const handleOpenBuyModal = () => {
    setShowBuyModal(true);
  };

  const handleCloseBuyModal = () => {
    setShowBuyModal(false);
  };

  const handleMobileSearchToggle = () => {
    setShowMobileSearch(!showMobileSearch);
    if (!showMobileSearch) {
      setTimeout(() => {
        const input = document.querySelector('.mobile-search-input');
        if (input) input.focus();
      }, 100);
    }
  };

  const handleOpenQRModal = () => {
    setShowQRModal(true);
  };

  const handleCloseQRModal = () => {
    setShowQRModal(false);
  };

  const downloadQRCode = () => {
    const canvas = document.querySelector('#qr-code-canvas canvas');
    if (canvas) {
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${username}-store-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const copyLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      alert(t.copyLink + '!');
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy link');
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${username}'s Store`,
          text: 'Check out this amazing store!',
          url: currentUrl,
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      copyLinkToClipboard();
    }
  };

  const getIconClass = (platform) => {
    const icons = {
      facebook: 'fab fa-facebook',
      telegram: 'fab fa-telegram',
      phone: 'fas fa-phone',
      tiktok: 'fab fa-tiktok',
      instagram: 'fab fa-instagram',
      whatsapp: 'fab fa-whatsapp',
      twitter: 'fab fa-twitter',
      youtube: 'fab fa-youtube',
    };
    return icons[platform?.toLowerCase()] || 'fas fa-link';
  };

  return (
    <>
      {/* Google Fonts - English & Khmer */}
      <link 
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Noto+Sans+Khmer:wght@300;400;500;600;700&display=swap" 
        rel="stylesheet" 
      />
      <link 
        rel="stylesheet" 
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" 
      />
      
      <style jsx>{`
        .navbar-font {
          font-family: ${language === 'km' ? "'Noto Sans Khmer', 'Inter', sans-serif" : "'Inter', sans-serif"};
        }
        .khmer-text {
          font-family: 'Noto Sans Khmer', sans-serif;
        }
        .khmer-input::placeholder {
          font-family: ${language === 'km' ? "'Noto Sans Khmer', sans-serif" : "'Inter', sans-serif"};
        }
      `}</style>

      <nav className="bg-white shadow-sm sticky top-0 z-50 navbar-font">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            {/* Logo Section */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3 cursor-pointer" onClick={handleOpenQRModal}>
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt={`${username}'s Store Logo`} 
                    className="h-12 md:h-16 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <>
                    <i className="fas fa-store text-lg md:text-xl" style={{ color: logoColor }}></i>
                    <h1 className="text-base md:text-lg font-semibold tracking-tight" style={{ color: logoColor }}>
                      {t.store}
                    </h1>
                  </>
                )}
              </div>
              
              {/* Navigation Links */}
              <div className="hidden md:flex items-center space-x-6">
                <a href="#home" className="text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors duration-200">
                  {t.home}
                </a>
                <a href="#products" className="text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors duration-200">
                  {t.products}
                </a>
                <a href="#contact" className="text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors duration-200">
                  {t.contact}
                </a>
              </div>
            </div>
            
            {/* Desktop Search and Buttons */}
            <div className="hidden md:flex items-center gap-3" ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className="relative">
                <input
                  type="text"
                  placeholder={t.search}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64 px-4 py-2 pr-20 border border-gray-200 text-gray-700 focus:outline-none focus:border-gray-300 text-sm khmer-input"
                  style={{ backgroundColor: '#f9fafb' }}
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                  {searchTerm && (
                    <button type="button" onClick={handleClearSearch} className="text-gray-400 hover:text-gray-600">
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  )}
                  <button type="submit" className="text-gray-400 hover:text-gray-600">
                    <i className="fas fa-search"></i>
                  </button>
                </div>
                
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-lg w-80 max-h-96 overflow-y-auto z-50 rounded-lg">
                    {searchResults.map((product) => (
                      <div
                        key={product.id}
                        onClick={() => handleProductClick(product)}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                      >
                        <img 
                          src={product.images && product.images[0] ? `https://easymenu-backend-4w2d.onrender.com${product.images[0]}` : 'https://via.placeholder.com/40x40'}
                          alt={product.title}
                          className="w-10 h-10 object-cover rounded"
                        />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-800">{product.title}</h4>
                          <p className="text-xs text-gray-500">${product.discount_price || product.price}</p>
                        </div>
                      </div>
                    ))}
                    <div onClick={handleSearchSubmit} className="p-2 text-center text-sm text-gray-600 hover:bg-gray-50 cursor-pointer">
                      {t.viewAll}
                    </div>
                  </div>
                )}
              </form>
              
              <button 
                onClick={handleOpenQRModal} 
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors duration-200 rounded-full hover:bg-gray-100"
                title={t.shareStore}
              >
                <i className="fas fa-qrcode text-lg"></i>
              </button>
              
              <button 
                onClick={handleOpenBuyModal} 
                className="px-5 py-2 text-white font-medium text-sm rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md"
                style={{ backgroundColor: primaryColor }}
              >
                <i className="fas fa-shopping-cart mr-2"></i> {t.buyNow}
              </button>
            </div>
            
            {/* Mobile Buttons */}
            <div className="flex md:hidden items-center gap-1">
              <button onClick={handleMobileSearchToggle} className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors">
                <i className="fas fa-search text-lg"></i>
              </button>
              <button onClick={handleOpenQRModal} className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors">
                <i className="fas fa-qrcode text-lg"></i>
              </button>
              <button onClick={handleOpenBuyModal} className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors">
                <i className="fas fa-shopping-cart text-lg"></i>
              </button>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
                className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'} text-lg`}></i>
              </button>
            </div>
          </div>
          
          {/* Mobile Search */}
          {showMobileSearch && (
            <div className="md:hidden py-2" ref={mobileSearchRef}>
              <form onSubmit={handleSearchSubmit} className="relative">
                <input
                  type="text"
                  placeholder={t.search}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pr-20 border border-gray-200 text-gray-700 text-sm rounded-lg khmer-input"
                  style={{ backgroundColor: '#f9fafb' }}
                  autoFocus
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                  {searchTerm && (
                    <button type="button" onClick={handleClearSearch} className="text-gray-400">
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  )}
                  <button type="submit" className="text-gray-400">
                    <i className="fas fa-search"></i>
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {/* Mobile Menu */}
          <div className={`md:hidden transition-all duration-300 ${mobileMenuOpen ? 'block' : 'hidden'}`}>
            <div className="pb-4 space-y-1">
              <a href="#home" className="block py-2 px-4 text-gray-600 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                {t.home}
              </a>
              <a href="#products" className="block py-2 px-4 text-gray-600 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                {t.products}
              </a>
              <a href="#contact" className="block py-2 px-4 text-gray-600 hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                {t.contact}
              </a>
            </div>
          </div>
        </div>
      </nav>
      
      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={handleCloseQRModal}>
          <div className="bg-white max-w-md w-full rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">{t.shareStore}</h3>
                <button onClick={handleCloseQRModal} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors">
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
              
              <div className="text-center">
                <div className="flex justify-center mb-4" id="qr-code-canvas">
                  <QRCodeCanvas
                    value={currentUrl}
                    size={qrSize}
                    level="H"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor={primaryColor}
                  />
                </div>
                
                <div className="flex justify-center gap-2 mb-4">
                  <button onClick={() => setQrSize(Math.max(150, qrSize - 20))} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">-</button>
                  <span className="text-sm">{qrSize}x{qrSize}</span>
                  <button onClick={() => setQrSize(Math.min(300, qrSize + 20))} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">+</button>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-500 break-all">{currentUrl}</p>
                </div>
                
                <div className="flex gap-2">
                  <button onClick={downloadQRCode} className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium">
                    {t.download}
                  </button>
                  <button onClick={copyLinkToClipboard} className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium">
                    {t.copyLink}
                  </button>
                  <button onClick={shareLink} className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium">
                    {t.share}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Search Page Modal */}
      {showSearchPage && (
        <SearchPage
          products={products}
          searchTerm={searchTerm}
          onClose={handleCloseSearchPage}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          onProductClick={handleProductClick}
          language={language}
        />
      )}
      
      {/* Buy Now Modal */}
      {showBuyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={handleCloseBuyModal}>
          <div className="bg-white max-w-md w-full rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="text-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
                  <i className="fas fa-shopping-cart text-2xl" style={{ color: primaryColor }}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.readyToPurchase}</h3>
                <p className="text-gray-500 text-sm">{t.contactUs}</p>
              </div>
              
              {contacts && contacts.length > 0 ? (
                <div className="space-y-3 mb-6">
                  {contacts.map((contact) => (
                    <a
                      key={contact.id}
                      href={contact.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-xl hover:border-gray-300 transition-all hover:shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <i className={`${getIconClass(contact.platform)} text-xl`} style={{ color: primaryColor }}></i>
                        <span className="text-gray-700 font-medium capitalize">{contact.platform}</span>
                      </div>
                      <i className="fas fa-arrow-right text-gray-400"></i>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500">{t.noContacts}</p>
                </div>
              )}
              
              <button 
                onClick={handleCloseBuyModal} 
                className="w-full py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm font-medium"
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
